import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { TenantAuthProvider } from './contexts/TenantAuthContext';
import { GlobalLoaderProvider } from './contexts/GlobalLoaderContext';
import GlobalLoaderWrapper from './components/ui/GlobalLoaderWrapper';
import { ToastProvider } from './components/ui/Toaster';
import ProtectedRoute from './components/auth/ProtectedRoute';
import TenantProtectedRoute from './components/auth/TenantProtectedRoute';
import AdminHostRedirect from './components/auth/AdminHostRedirect';
import OrganizationOnboardingGate from './components/auth/OrganizationOnboardingGate';
import { PermissionProtectedRoute } from './components/auth/PermissionProtectedRoute';
import ModernLayout from './components/layout/ModernLayout';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from './config/google';
import { TenantRedirectHandler } from './components/auth/TenantRedirectHandler';
import { useAuth } from './contexts/AuthContext';
import RootRouteHandler from './components/RootRouteHandler';
import RouteGateFallback from './components/ui/RouteGateFallback';
// Legal pages: eager import so public /terms and /privacy never show the heavy route skeleton
// while a lazy chunk downloads (better UX for static, link-heavy flows).
import TermsOfUse from './pages/legal/TermsOfUse';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TenantTermsOfUse from './pages/legal/TenantTermsOfUse';
import TenantPrivacyPolicy from './pages/legal/TenantPrivacyPolicy';

// Lazy-loaded pages – loaded only when their route is visited (smaller initial bundle)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/auth/LoginPage'));
const Signup = lazy(() => import('./pages/auth/SignupPage'));
const LandlordPhoneVerify = lazy(() => import('./pages/auth/LandlordPhoneVerify'));
const SignupPendingApproval = lazy(() => import('./pages/auth/SignupPendingApproval'));
const OrgSetupPage = lazy(() => import('./pages/auth/OrgSetupPage'));

const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const ForcePasswordChange = lazy(() => import('./pages/auth/ForcePasswordChange'));
const TenantForgotPassword = lazy(() => import('./pages/auth/TenantForgotPassword'));
const TenantOnboarding = lazy(() => import('./pages/tenant/TenantOnboarding'));
const TenantLogin = lazy(() => import('./components/tenant/TenantLogin'));
const TenantPhoneVerify = lazy(() => import('./pages/tenant/TenantPhoneVerify'));
const AcceptInvitation = lazy(() => import('./pages/auth/AcceptInvitation'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Properties = lazy(() => import('./pages/properties/Properties'));
const PropertyUnits = lazy(() => import('./pages/properties/PropertyUnits'));
const Tenants = lazy(() => import('./pages/tenants/Tenants'));
const Payments = lazy(() => import('./pages/payments/Payments'));
const MessageManagement = lazy(() => import('./pages/messages/MessageManagement'));
const TenantPortal = lazy(() => import('./pages/tenant/TenantPortal'));
const TenantSettings = lazy(() => import('./pages/tenant/TenantSettings'));
const DepositPayment = lazy(() => import('./pages/deposit/DepositPayment'));
const VerifyPayment = lazy(() => import('./pages/payments/VerifyPayment'));
const PaymentLinkPage = lazy(() => import('./pages/PaymentLinkPage'));
const PropertyLocationPinPage = lazy(() => import('./pages/properties/PropertyLocationPinPage'));
const LandlordBilling = lazy(() => import('./pages/billing/LandlordBilling'));

// App Check is automatically activated when initialized
if (process.env.NODE_ENV === 'development') console.log('App Check initialized');

// Test component to verify ProtectedRoute
const TestComponent = () => {
  console.log('🧪 TestComponent rendered');
  return <div>Test Component - If you see this, ProtectedRoute is working!</div>;
};

// Auth Debug Component
const AuthDebug = () => {
  const { user, loading, isAuthenticated } = useAuth();

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>🔍 Auth Debug Info</h2>
      <pre>
        {JSON.stringify({
          loading,
          isAuthenticated,
          timestamp: new Date().toISOString()
        }, null, 2)}
      </pre>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <GlobalLoaderProvider>
            <GlobalLoaderWrapper>
              <Router>
                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || 'not-configured.apps.googleusercontent.com'}>
                <AuthProvider>
                  <TenantRedirectHandler>
                    <Suspense fallback={<RouteGateFallback variant="chunk" />}>
                      <Routes>
                        {/* Root route - Smart handler: Properties for landlords, LandingPage for unauthenticated */}
                        {/* RootRouteHandler handles both authenticated and unauthenticated cases */}
                        <Route path="/" element={<RootRouteHandler />} />
                        <Route path="/landing" element={<LandingPage />} />
                        <Route path="/onboarding" element={<Navigate to="/signup" replace />} />
                        <Route path="/login" element={
                          <TenantAuthProvider>
                            <Login />
                          </TenantAuthProvider>
                        } />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/verify-phone" element={<LandlordPhoneVerify />} />
                        <Route path="/signup-pending-approval" element={<SignupPendingApproval />} />
                        <Route path="/org-setup" element={
                          <ProtectedRoute>
                            <OrgSetupPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/force-change-password" element={
                          <ProtectedRoute>
                            <ForcePasswordChange />
                          </ProtectedRoute>
                        } />
                        <Route path="/terms" element={<TermsOfUse />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        {/* Tenant reset password route - kept for backward compatibility, but unified page handles both */}
                        <Route path="/tenant/reset-password" element={<ResetPassword />} />
                        <Route path="/accept-invitation" element={<AcceptInvitation />} />
                        <Route path="/deposit-payment/:token" element={<DepositPayment />} />
                        <Route path="/verify-payment/:token" element={<VerifyPayment />} />
                        <Route path="/pay/:token" element={<PaymentLinkPage />} />
                        <Route path="/location-pin/:token" element={<PropertyLocationPinPage />} />

                        {/* Debug route */}
                        <Route path="/debug" element={<AuthDebug />} />

                        {/* Test route to verify ProtectedRoute */}
                        <Route path="/test" element={
                          <ProtectedRoute>
                            <TestComponent />
                          </ProtectedRoute>
                        } />

                        {/* Platform admin lives on admin.* — redirect only */}
                        <Route path="/admin/*" element={<AdminHostRedirect />} />
                        <Route path="/admin" element={<AdminHostRedirect />} />

                        {/* Protected nested routes - These work alongside RootRouteHandler */}
                        <Route path="/properties/:propertyId/units" element={
                          <ProtectedRoute>
                            <ModernLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={
                            <PermissionProtectedRoute requiredPermission="properties">
                              <PropertyUnits />
                            </PermissionProtectedRoute>
                          } />
                        </Route>
                        <Route path="/tenants" element={
                          <ProtectedRoute>
                            <ModernLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={
                            <PermissionProtectedRoute requiredPermission="tenants">
                              <Tenants />
                            </PermissionProtectedRoute>
                          } />
                        </Route>
                        <Route path="/payments" element={
                          <ProtectedRoute>
                            <ModernLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={
                            <PermissionProtectedRoute requiredPermission="payments">
                              <Payments />
                            </PermissionProtectedRoute>
                          } />
                        </Route>
                        <Route path="/messages" element={
                          <ProtectedRoute>
                            <ModernLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={<MessageManagement />} />
                        </Route>
                        <Route path="/billing" element={
                          <ProtectedRoute>
                            <ModernLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={<LandlordBilling />} />
                        </Route>

                        {/* Tenant Portal Routes - Separate from landlord routes */}
                        <Route path="/tenant/terms" element={<TenantTermsOfUse />} />
                        <Route path="/tenant/privacy" element={<TenantPrivacyPolicy />} />
                        <Route path="/tenant/login" element={
                          <TenantAuthProvider>
                            <TenantLogin />
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/forgot-password" element={<TenantForgotPassword />} />
                        <Route path="/tenant/onboarding" element={<TenantOnboarding />} />
                        <Route path="/tenant/verify-phone" element={
                          <TenantAuthProvider>
                            <TenantPhoneVerify />
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/portal/:tenantId" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <TenantPortal />
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        {/* New portal sub-routes */}
                        <Route path="/tenant/portal/:tenantId/lease" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <div>Lease Details Page - Coming Soon</div>
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/portal/:tenantId/payments" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <div>Payments Page - Coming Soon</div>
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/portal/:tenantId/messages" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <div>Messages Page - Coming Soon</div>
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/portal/:tenantId/notifications" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <div>Notifications Page - Coming Soon</div>
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/portal/:tenantId/settings" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <TenantSettings />
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/portal/:tenantId/profile" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <div>Profile Page - Coming Soon</div>
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        <Route path="/tenant/portal/:tenantId/help" element={
                          <TenantAuthProvider>
                            <TenantProtectedRoute>
                              <div>Help & Support Page - Coming Soon</div>
                            </TenantProtectedRoute>
                          </TenantAuthProvider>
                        } />
                        {/* Redirect old portal route to login for better UX */}
                        <Route path="/tenant/portal" element={
                          <Navigate to="/login" replace />
                        } />
                      </Routes>
                    </Suspense>
                  </TenantRedirectHandler>
                </AuthProvider>
                </GoogleOAuthProvider>
              </Router>
            </GlobalLoaderWrapper>
          </GlobalLoaderProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
